import { useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from '@tanstack/react-router';
import type { Audience, PriceValue, PricingPlan } from '~/content/pricing';
import { Grid } from '~/components/ui';
import { Section } from '~/components/layout';
import { cn } from '~/lib/cn';
import { useContent, useLocalizedPath } from '~/lib/useLanguage';

/** Segmented Personal / Business switch (Figma pricing toggle). */
export function AudienceToggle({
  value,
  onChange,
}: {
  value: Audience;
  onChange: (next: Audience) => void;
}) {
  const { ui } = useContent();
  const options: { id: Audience; label: string }[] = [
    { id: 'personal', label: ui.pricing.personal },
    { id: 'business', label: ui.pricing.business },
  ];
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.id === value),
  );

  const btnRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const measuredRef = useRef(false);
  const [pill, setPill] = useState<{ left: number; width: number } | null>(null);
  const [reduced, setReduced] = useState(false);
  // The single indicator jumps into place on its first measured layout, then
  // animates for every subsequent slide/resize so it never eases in from 0.
  const [animate, setAnimate] = useState(false);

  useLayoutEffect(() => {
    setReduced(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  // Measure the active button (offsets are relative to the positioned
  // container) and drive the indicator to sit behind it. Runs on mount and
  // whenever the selected value changes.
  useLayoutEffect(() => {
    const el = btnRefs.current[activeIndex];
    if (!el) return;
    setPill({ left: el.offsetLeft, width: el.offsetWidth });
    if (!measuredRef.current) {
      measuredRef.current = true;
      requestAnimationFrame(() => setAnimate(true));
    }
  }, [activeIndex]);

  // Recompute if the container resizes (font/layout shifts or viewport changes)
  // so the indicator keeps tracking the active option's live geometry.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const el = btnRefs.current[activeIndex];
      if (el) setPill({ left: el.offsetLeft, width: el.offsetWidth });
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, [activeIndex]);

  return (
    <div ref={containerRef} className="relative inline-flex rounded-pill bg-grey-50 p-1">
      {/* Single white pill that slides + resizes behind the labels. */}
      {pill && (
        <div
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-1 bottom-1 left-0 z-0 rounded-pill bg-white shadow-soft',
            animate && !reduced && 'transition-[transform,width] duration-300 ease-nav',
          )}
          style={{
            transform: `translateX(${pill.left}px)`,
            width: pill.width,
            willChange: 'transform, width',
          }}
        />
      )}
      {options.map((option, i) => (
        <button
          key={option.id}
          ref={(el) => {
            btnRefs.current[i] = el;
          }}
          type="button"
          onClick={() => onChange(option.id)}
          className={cn(
            'relative z-10 cursor-pointer rounded-pill px-5 py-4 text-[16px] leading-[1.4] font-medium tracking-[0.192px] transition-colors duration-300 ease-nav',
            value === option.id ? 'text-ink' : 'text-grey-500 hover:text-ink',
          )}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

// Grid placement shared by the plan header and every feature row so the
// value columns always line up (label 1-4, first plan 6-7, second plan 8-9).
const labelCol = 'col-span-9 md:col-span-4';
const planStarts = ['md:col-start-6', 'md:col-start-8'];
const planCol = (i: number) => cn('col-span-9 md:col-span-2', planStarts[i]);

/** Runs of Arabic script, including the whitespace between adjacent words. */
const ARABIC_RUN =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+(?:[\s\u060C]+[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+)*/gu;

/**
 * Split a fee value into Arabic and non-Arabic runs.
 *
 * Testing the string as a whole is not enough. Several values mix the two — the
 * Business SWIFT fee reads "0.3% ** (الحد الأدنى 14.99 دولار، …)" — and leaving
 * the whole run in RTL context lets the bidi algorithm move the percentage,
 * mirror the brackets, or split the min/max range apart. Isolating each
 * non-Arabic run instead keeps numbers, currency and rail names in their own
 * direction while the Arabic words follow the page.
 *
 * A value with no Arabic at all ("$12.99", "0.5%-1.5%", "Free") comes back as a
 * single isolated run — exactly what the English table renders today.
 */
export function bidiSegments(text: string): { text: string; isolate: boolean }[] {
  const segments: { text: string; isolate: boolean }[] = [];
  let cursor = 0;

  for (const match of text.matchAll(ARABIC_RUN)) {
    if (match.index > cursor) {
      segments.push({ text: text.slice(cursor, match.index), isolate: true });
    }
    segments.push({ text: match[0], isolate: false });
    cursor = match.index + match[0].length;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), isolate: true });

  return segments;
}

/** Renders a value with every non-Arabic run pinned to LTR. */
function BidiText({ text }: { text: string }) {
  return (
    <>
      {bidiSegments(text).map((segment, i) =>
        segment.isolate ? (
          <span key={i} dir="ltr">
            {segment.text}
          </span>
        ) : (
          <span key={i}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function ValueCell({ value }: { value: PriceValue }) {
  const red = value.free || value.highlight;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 text-body font-medium',
        red ? 'text-red-500' : 'text-ink',
        value.info && 'ps-5',
      )}>
      {value.down && <DownArrow />}
      <BidiText text={value.text} />
      {value.info && <InfoIcon title={value.info} />}
    </span>
  );
}

export function PricingTable({ audience = 'personal' }: { audience?: Audience }) {
  const { pricing, ui } = useContent();
  const localizedPathFor = useLocalizedPath();
  const { plans, groups } = pricing[audience];

  const valueCell = (i: number) => cn(planCol(i), 'flex items-center justify-center py-5');

  // Mobile stacks each feature as a full-width title with the per-plan values in
  // aligned, centered columns below it. Business has a single plan, Personal two.
  const mobileValueCols = plans.length === 1 ? 'grid-cols-1' : 'grid-cols-2';

  return (
    <Section className="!pt-[104px]">
      {/* Desktop (md+): the original label + value-column grid, untouched. */}
      <div className="hidden md:block">
        <Grid className="gap-y-0">
          {/* Plan headers — sticky below the site header (h-16) so the plan names
              + prices stay in view while scrolling the table. */}
          <div className="sticky top-16 z-20 col-span-9 bg-white pt-2 pb-5">
            <Grid>
              {plans.map((plan, i) => (
                <div
                  key={plan.name}
                  className={cn(planCol(i), 'pricing-rise flex flex-col items-center gap-4')}
                  style={{ '--stagger': `${i * 60}ms` } as CSSProperties}>
                  <PlanHeader plan={plan} />
                </div>
              ))}
            </Grid>
          </div>

          {groups.map((group, gi) => (
            <div
              key={group.title}
              className={cn('pricing-rise col-span-9', gi === 0 ? 'mt-10' : 'mt-14')}
              style={{ '--stagger': `${120 + gi * 90}ms` } as CSSProperties}>
              <Grid className="min-h-[63px]">
                <div className={cn(labelCol, 'flex items-center py-5')}>
                  <h3 className="text-h4 font-semibold text-ink">{group.title}</h3>
                </div>
              </Grid>
              {group.rows.map((row) => (
                <Grid
                  key={row.label}
                  className="border-t border-black-6 transition-colors duration-[350ms] ease-nav hover:bg-[rgba(0,0,0,0.02)]">
                  <div className={cn(labelCol, 'flex items-center py-5')}>
                    <p className="text-body text-grey-900">{row.label}</p>
                  </div>
                  {row.values.map((value, i) => (
                    <div key={i} className={valueCell(i)}>
                      <ValueCell value={value} />
                    </div>
                  ))}
                </Grid>
              ))}
            </div>
          ))}

          {/* Close the table with a bottom divider. */}
          <div className="col-span-9 border-t border-black-6" />

          <div className="col-span-9 mt-12">
            <Link
              to={localizedPathFor('/terms-conditions')}
              className="text-body-m text-grey-500 transition-colors hover:text-ink">
              {ui.pricing.seeAllTerms}
            </Link>
          </div>
        </Grid>
      </div>

      {/* Mobile (< md): plan names sit as columns on top; each feature title
          spans the full width with its per-plan values centered beneath. */}
      <div className="md:hidden">
        {/* Plan headers as aligned columns, sticky below the site header. */}
        <div className="sticky top-16 z-20 bg-white pt-2 pb-5">
          <div className={cn('grid gap-5', mobileValueCols)}>
            {plans.map((plan, i) => (
              <div
                key={plan.name}
                className="pricing-rise flex flex-col items-center gap-4 text-center"
                style={{ '--stagger': `${i * 60}ms` } as CSSProperties}>
                <PlanHeader plan={plan} />
              </div>
            ))}
          </div>
        </div>

        {groups.map((group, gi) => (
          <div
            key={group.title}
            className={cn('pricing-rise', gi === 0 ? 'mt-10' : 'mt-14')}
            style={{ '--stagger': `${120 + gi * 90}ms` } as CSSProperties}>
            {/* Group title spans the full width as a section separator. */}
            <div className="py-5">
              <h3 className="text-h4 font-semibold text-ink">{group.title}</h3>
            </div>
            {group.rows.map((row) => (
              <div
                key={row.label}
                className="border-t border-black-6 pt-5 pb-7 transition-colors duration-[350ms] ease-nav hover:bg-[rgba(0,0,0,0.02)]">
                {/* Feature title spans both columns above its values. */}
                <p className="text-body text-grey-900">{row.label}</p>
                {/* Per-plan values, each centered in its column. */}
                <div className={cn('mt-6 grid gap-5', mobileValueCols)}>
                  {row.values.map((value, i) => (
                    <div key={i} className="flex items-center justify-center text-center">
                      <ValueCell value={value} />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}

        {/* Close the table with a bottom divider. */}
        <div className="border-t border-black-6" />

        <div className="mt-12">
          <Link
            to={localizedPathFor('/terms-conditions')}
            className="text-body-m text-grey-500 transition-colors hover:text-ink">
            {ui.pricing.seeAllTerms}
          </Link>
        </div>
      </div>
    </Section>
  );
}

function PlanHeader({ plan }: { plan: PricingPlan }) {
  return (
    <>
      <p className="text-h4 font-semibold text-ink">{plan.name}</p>
      {/* `cadence` is the rendered suffix, not a description of it — the hardcoded
          "/m" it replaced meant a translated table still read "$0.00/m".
          Amount and cadence form one run; without dir="ltr" an RTL page renders
          "$0.00/m" as "m/$0.00". */}
      <p dir="ltr" className="mt-1 text-body font-semibold text-ink">
        {plan.price}
        <span className="text-body-s font-normal text-grey-500">{plan.cadence}</span>
      </p>
    </>
  );
}

function InfoIcon({ title }: { title: string }) {
  return (
    <span title={title} className="inline-flex text-red-500">
      <svg viewBox="0 0 16 16" fill="none" className="size-3.5" aria-hidden="true">
        <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.2" />
        <path d="M8 7.2v3.4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
        <circle cx="8" cy="5.2" r="0.8" fill="currentColor" />
      </svg>
    </span>
  );
}

function DownArrow() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="size-4"
      aria-hidden="true">
      <path d="M8 3v10M4 9l4 4 4-4" />
    </svg>
  );
}
